import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ORIGIN = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://www.shandsons.in").replace(/\/+$/, "");
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const metadataCache = new Map();
const PRIVATE_PATHS = new Set(["/cart", "/wishlist", "/checkout", "/enquiry/received", "/buyer-access"]);

const setMeta = (attr, key, content) => {
  const matches = [...document.head.querySelectorAll(`meta[${attr}="${key}"]`)];
  const el = matches.shift() || document.createElement("meta");
  matches.forEach((duplicate) => duplicate.remove());
  el.setAttribute(attr, key);
  el.setAttribute("content", content || "");
  if (!el.parentNode) document.head.appendChild(el);
};

const absolute = (value) => {
  if (!value) return ORIGIN + "/assets/brand-social.png";
  try { return new URL(value, ORIGIN).href; } catch { return ORIGIN + "/assets/brand-social.png"; }
};

function applyMetadata(data) {
  document.title = data.title;
  setMeta("name", "description", data.description);
  setMeta("name", "robots", data.robots);
  for (const [key, value] of Object.entries({ type: data.type || "website", site_name: "S H & SONS ®",
    title: data.title, description: data.description, url: data.canonical,
    image: absolute(data.image), "image:alt": data.image_alt || "S H & SONS ®" })) {
    setMeta("property", "og:" + key, value);
  }
  for (const [key, value] of Object.entries({ card: "summary_large_image", title: data.title,
    description: data.description, image: absolute(data.image), "image:alt": data.image_alt || "S H & SONS ®" })) {
    setMeta("name", "twitter:" + key, value);
  }
  const links = [...document.head.querySelectorAll('link[rel="canonical"]')];
  const canonical = links.shift() || document.createElement("link");
  links.forEach((duplicate) => duplicate.remove());
  canonical.rel = "canonical";
  canonical.href = data.canonical;
  if (!canonical.parentNode) document.head.appendChild(canonical);
  document.head.querySelectorAll('script#page-jsonld').forEach((el) => el.remove());
  if (data.json_ld) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-jsonld";
    script.textContent = JSON.stringify(data.json_ld);
    document.head.appendChild(script);
  }
}

function normalizeSchema(value, key = "") {
  if (Array.isArray(value)) return value.map((item) => normalizeSchema(item, key));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([name, item]) => [name, normalizeSchema(item, name)]));
  if (typeof value === "string" && ["url", "@id", "image", "logo", "item"].includes(key)) {
    const local = value.startsWith(window.location.origin) ? value.slice(window.location.origin.length) || "/" : value;
    if (key === "logo" && local === "/assets/logo-black.svg") return ORIGIN + "/assets/shsons-emblem-original.svg";
    return absolute(local);
  }
  return value;
}

async function routeMetadata(path) {
  const cached = metadataCache.get(path);
  if (cached && Date.now() - cached.at < 30_000) return cached.promise;
  const promise = fetch(BACKEND + "/api/seo?path=" + encodeURIComponent(path),{headers:document.documentElement.dataset.showroomPreview?{"X-Showroom-Preview":"1"}:{}})
    .then((response) => { if (!response.ok) throw new Error("Metadata unavailable"); return response.json(); })
    .catch((error) => { metadataCache.delete(path); throw error; });
  metadataCache.set(path, { at: Date.now(), promise });
  return promise;
}

export default function SEO({ title, description, image, jsonLd, noindex = false }) {
  const { pathname, search } = useLocation();
  // Serialize inline schema props so unrelated renders don't repeat metadata work.
  const schema = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    let active = true;
    const path = pathname.replace(/\/+$/, "") || "/";
    const preview = document.querySelector('meta[name="shsons:preview"]')?.content === "true"
      || ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);
    const privatePage = noindex || PRIVATE_PATHS.has(path) || path === "/admin" || path.startsWith("/admin/");
    const queryPage = search && ["/shop", "/enquiry", "/catalogue"].includes(path);
    const canonicalPath = path === "/checkout" ? "/enquiry" : path;
    applyMetadata({ title: title || "S H & SONS ®", description: description || "", image,
      canonical: ORIGIN + canonicalPath, robots: preview || privatePage ? "noindex, nofollow" : queryPage ? "noindex, follow" : "index, follow, max-image-preview:large",
      json_ld: privatePage ? null : schema ? normalizeSchema(JSON.parse(schema)) : null });
    routeMetadata(pathname + search).then((data) => {
      if (!active) return;
      applyMetadata({ ...data, ...(preview || noindex ? { robots: "noindex, nofollow" } : {}) });
    }).catch(() => {});
    return () => { active = false; };
  }, [pathname, search, title, description, image, schema, noindex]);
  return null;
}
